import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Prop, Ref, Watch } from "@eastsideco/salvo-ts";
import { cart } from "@/theme/Cart";
import { formatMoney } from "@/utils/formatting";
import { ProductOptionPicker } from "./ProductOptionPicker";
import { QuantitySpinner } from "../QuantitySpinner/QuantitySpinner";
import { setVariantUrlParam, getQuantityMax } from "@/utils/products";

/**
 * A form which allows customers to select product options and then
 * add the matching variant and quantity to the cart.
 */
@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop optionStyle!: 'dropdown'|'buttons';
    @Prop variantsQty!: string;
    @Prop strings!: string;
    @Ref({ required: false }) options!: ProductOptionPicker;
    @Ref submitButton!: HTMLButtonElement;
    @Ref submitButtonText!: HTMLSpanElement;
    @Ref error!: HTMLElement;
    @Ref price!: HTMLElement[];
    @Ref({ required: false }) comparePrice!: HTMLElement;
    @Ref quantity!: QuantitySpinner|null;

    @Ref({required: false}) tests!: HTMLElement[];

    protected loading: boolean = false;
    protected selectedVariant: Shopify.Liquid.Variant|null = null;
    protected quantityVal: number = 1;
    protected maxQty: string = '1';
    protected selectedCartQty: number = 0;

    init() {
        if (this.options) {
            this.$subscribe(this.options.onSelectedVariantChange, (variant) => this.selectedVariant = variant);
            this.selectedVariant = this.options.selectedVariant;
        }

        // Quantity Funtions
        this.setQuantityMax();
        document.addEventListener('cartChanged', (e: Event) => {
            this.setQuantityMax();
        });

        this.checkQuantityChange();

        this.$listen(this.submitButton, 'click', () => {
            this.addToCart();
        });
    }

    protected showError(msg: string|null): void {
        this.error.textContent = msg;
        this.error.classList.add('is-active');
        this.error.style.display = msg ? '' : 'none';
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        setVariantUrlParam(this.selectedVariant?.id || null);
        this.setQuantityMax();
        this.handlePrice();
        this.handleSku();
    }

    @Watch('loading')
    protected handleLoading() {
        this.submitButton.disabled = this.loading;
    }

    /**
     *  Update Price;
     * @returns
     */
    handlePrice() {
        if (this.selectedVariant ===  null) { return; }
        const updatedPrice = formatMoney(this.selectedVariant.price) as string;
        const comparePrice = formatMoney(this.selectedVariant.compare_at_price) as string;

        for (const price of this.price) {
            price.innerHTML = updatedPrice;
        }

        if (comparePrice == null) { return; }

        if (this.comparePrice && this.selectedVariant.compare_at_price !== 0) {
            this.comparePrice.innerHTML = comparePrice;
        } else {
            this.comparePrice.innerHTML = '';
        }
    }

    handleSku() {
        if (this.selectedVariant ===  null) { return; }
        const skuElement = document.querySelector('.product-main__sku') as HTMLElement;
        const skuNumberElement = skuElement?.querySelector('.product-main__sku-number') as HTMLElement;
        if (this.selectedVariant.sku) {
            if (skuElement && skuNumberElement) {
                skuElement.classList.remove('hidden');
                skuNumberElement.innerText = this.selectedVariant.sku;
            }
        } else {
            if (skuElement) {
                skuElement.classList.add('hidden');
            }
        }
    }

    /**
     * Set maximum variant quantity on variant change.
     */
    protected setQuantityMax() {
        this.maxQty = getQuantityMax(this.selectedVariant?.id || null, this.variantsQty);

        this.selectedCartQty = this.checkCartQty(this.selectedVariant);

        if (this.selectedCartQty > 0) {
            this.maxQty = String(~~this.maxQty - this.selectedCartQty);
        }

        this.handleQtyInput();
        this.handleQtyButtons();

        if (~~this.maxQty === 0) {
            this.handleOutOfStock();
            this.quantity?.setAttribute('max', this.maxQty);
            this.quantity?.input.setAttribute('max', this.maxQty);
            this.quantity?.classList.add('visually-hidden');
            return;
        }

        if (this.quantity) {
            this.quantity.setAttribute('max', this.maxQty);
            this.quantity.input.setAttribute('max', this.maxQty);
            this.quantity.updateValue(1);
            this.quantity.classList.remove('visually-hidden');
            this.handleInStock();
        }
    }

    handleQtyInput() {
        const qtyInput = this.quantity?.input.value;

        if (qtyInput && (~~qtyInput > ~~this.maxQty)) {
            if (!this.quantity) { return; }
            this.quantity.input.value = this.maxQty;
            return;
        }
    }

    handleQtyButtons() {
        if (!this.quantity) { return; }

        if (~~this.quantity.input.value >= ~~this.maxQty) {
            this.quantity.increaseButton.disabled = true;
            return;
        }

        this.quantity.increaseButton.disabled = false;
    }

    /**
     * Check if selected variant is in cart? get quanity.
     * @param variant - selected variant.
     * @returns
     */
    protected checkCartQty(variant: Shopify.Liquid.Variant|null) {
        if (variant === null || cart.items.length === 0) { return 0; }

        let quantity = 0;
        for (const item of cart.items) {
            if (item.id === variant.id) {
                quantity = item.quantity;
            }
        }

        return quantity;
    }

    /**
     * If quantity switcher exists, update Quantity on value change.
     */
    protected checkQuantityChange() {
        if (this.quantity === null) { return; }
        this.$subscribe(this.quantity.onChange, (value) => {
            this.quantityVal = value;
        });
    }

    /**
     * Handle Add to Cart.
     */
    protected addToCart() {
        this.loading = true;
        const variant = this.selectedVariant;
        if (!variant) {
            this.showError(`Please select an option.`);
            return;
        }

        cart.addItem({
            id: variant.id,
            quantity: this.quantityVal,
        }).then((item) => {
            document.dispatchEvent(new Event('cartChanged'));
        }).catch((event: unknown | null) => {
            if (event instanceof Error) {
                this.showError(event.message);
            }
        });

        this.loading = false;
    }

    /**
     * Diasable AdToCart if variant is out of stock.
     */
    protected handleOutOfStock() {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('button--disabled');
        this.submitButtonText.innerHTML = this.getString('soldOut');
    }

    /**
     * Enable AddToCart.
     */
    protected handleInStock() {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('button--disabled');
        this.submitButtonText.innerHTML = this.getString('add');
    }

    /**
     * Get Language string set within the product template.
     * @param string
     * @returns
     */
    protected getString(string: string) {
        const strings = JSON.parse(this.strings);
        return strings[string];
    }
}
